// src/auth/AuthProvider.tsx
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { googleLogout } from "@react-oauth/google";

// src/auth/entitlements.ts
var APP_ROLES = ["none", "viewer", "operator", "admin"];
var ROLE_RANK = {
  none: 0,
  viewer: 1,
  operator: 2,
  admin: 3
};
var isAppRole = (value) => typeof value === "string" && APP_ROLES.includes(value);
var normalizeAppEntitlementManifest = (value) => {
  if (!value || typeof value !== "object") return void 0;
  const record = value;
  const roles = Object.fromEntries(
    Object.entries(
      record.roles && typeof record.roles === "object" ? record.roles : {}
    ).filter(([appId, role]) => appId.trim().length > 0 && isAppRole(role))
  );
  const groups = Array.isArray(record.groups) ? [...new Set(record.groups.filter((group) => typeof group === "string" && group.trim().length > 0))].map((group) => group.trim().toLowerCase()).sort() : [];
  return {
    groups,
    roles,
    source: typeof record.source === "string" ? record.source.slice(0, 120) : "not-materialized",
    revision: typeof record.revision === "string" ? record.revision.slice(0, 120) : "",
    syncedAt: typeof record.syncedAt === "string" && record.syncedAt.trim() ? record.syncedAt.slice(0, 120) : void 0
  };
};
var evaluateAppAccess = (manifest, appId, requiredRole = "viewer", options = {}) => {
  const defaultRole = options.defaultRole ?? "none";
  const enforcement = options.enforcement ?? "enforced";
  const grantedRole = manifest?.roles[appId] ?? defaultRole;
  const wouldAllow = ROLE_RANK[grantedRole] >= ROLE_RANK[requiredRole];
  const allowed = enforcement === "report_only" ? true : wouldAllow;
  return {
    appId,
    requiredRole,
    grantedRole,
    enforcement,
    allowed,
    wouldAllow,
    reason: enforcement === "report_only" ? wouldAllow ? "report_only_role_sufficient" : "report_only_role_required" : wouldAllow ? "role_sufficient" : "role_required",
    source: manifest?.source || "not-materialized",
    revision: manifest?.revision || ""
  };
};
var compareAppRoles = (left, right) => ROLE_RANK[left] - ROLE_RANK[right];

// src/auth/AuthProvider.tsx
import { jsx } from "react/jsx-runtime";
var AuthContext = createContext(void 0);
var DEFAULT_API_DOMAIN = "https://api.automations.easystreetbrands.com";
var normalizeExternalIdentity = (value) => {
  if (!value || typeof value !== "object") return void 0;
  const record = value;
  if (Object.keys(record).sort().join(",") !== "credentialVersion,issuer,provider,subject" || record.provider !== "cognito" || typeof record.issuer !== "string" || !/^https:\/\/cognito-idp\.[a-z0-9-]+\.amazonaws\.com\/[a-zA-Z0-9_-]+$/.test(record.issuer) || typeof record.subject !== "string" || !record.subject.trim() || typeof record.credentialVersion !== "string" || !/^[a-f0-9]{32}$/.test(record.credentialVersion)) return void 0;
  return { provider: "cognito", issuer: record.issuer, subject: record.subject, credentialVersion: record.credentialVersion };
};
var normalizeAuthUser = (value) => {
  if (!value || typeof value !== "object") return null;
  const record = value;
  if (typeof record.email !== "string" || !record.email.trim()) return null;
  const externalIdentity = normalizeExternalIdentity(record.externalIdentity);
  if (record.externalIdentity !== void 0 && !externalIdentity) return null;
  return {
    email: record.email.trim().toLowerCase(),
    name: typeof record.name === "string" && record.name.trim() ? record.name.trim() : record.email.trim().toLowerCase(),
    picture: typeof record.picture === "string" ? record.picture : void 0,
    dataEntitlements: record.dataEntitlements && typeof record.dataEntitlements === "object" ? record.dataEntitlements : void 0,
    appEntitlements: normalizeAppEntitlementManifest(record.appEntitlements),
    identityType: record.identityType === "vendor" ? "vendor" : void 0,
    externalIdentity: record.identityType === "vendor" ? externalIdentity : void 0,
    vendorAccess: record.identityType === "vendor" && record.vendorAccess && typeof record.vendorAccess === "object" ? record.vendorAccess : void 0
  };
};
var sanitizeBaseUrl = (value) => {
  if (!value) {
    return "";
  }
  const trimmed = String(value).trim();
  if (!trimmed) {
    return "";
  }
  return trimmed.replace(/\/+$/, "");
};
var readProcessEnv = (key) => {
  if (typeof globalThis === "undefined") {
    return void 0;
  }
  return globalThis.process?.env?.[key];
};
var inferBrand = (importMeta) => {
  const envBrand = importMeta?.env?.VITE_BRAND || importMeta?.env?.VITE_BRAND_ID || readProcessEnv("VITE_BRAND") || readProcessEnv("VITE_BRAND_ID");
  if (typeof envBrand === "string" && envBrand.trim()) {
    return envBrand.trim().toLowerCase();
  }
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname.toLowerCase();
    if (hostname.includes("hollow")) {
      return "hollow";
    }
    if (hostname.includes("marsmen")) {
      return "marsmen";
    }
  }
  return void 0;
};
var inferStage = (importMeta) => {
  const explicitStage = importMeta?.env?.VITE_API_STAGE || importMeta?.env?.VITE_STAGE || readProcessEnv("VITE_API_STAGE") || readProcessEnv("VITE_STAGE");
  if (explicitStage && explicitStage.trim()) {
    return explicitStage.trim();
  }
  const envName = importMeta?.env?.VITE_ENVIRONMENT || importMeta?.env?.NODE_ENV || readProcessEnv("VITE_ENVIRONMENT") || readProcessEnv("NODE_ENV");
  if (envName) {
    const normalized = envName.trim().toLowerCase();
    if (normalized.startsWith("prod")) {
      return "prod";
    }
    if (normalized.startsWith("stag")) {
      return "staging";
    }
    if (normalized.startsWith("dev")) {
      return "development";
    }
  }
  return "prod";
};
var buildUnifiedBaseUrl = (brand, stage) => {
  if (!brand) {
    return "";
  }
  const stageName = stage && stage.trim() ? stage.trim() : "prod";
  return `${DEFAULT_API_DOMAIN}/${brand}/${stageName}`;
};
var resolveApiBaseUrl = (explicit) => {
  const sanitizedExplicit = sanitizeBaseUrl(explicit);
  if (sanitizedExplicit) {
    return sanitizedExplicit;
  }
  const runtimeConfig = typeof window !== "undefined" && window.__APP_CONFIG__ || {};
  if (runtimeConfig.apiBaseUrl) {
    return sanitizeBaseUrl(runtimeConfig.apiBaseUrl);
  }
  const importMeta = typeof import.meta !== "undefined" ? import.meta : void 0;
  const envBase = sanitizeBaseUrl(importMeta?.env?.VITE_API_BASE_URL);
  if (envBase) {
    return envBase;
  }
  const envUrl = sanitizeBaseUrl(importMeta?.env?.VITE_API_URL || importMeta?.env?.API_ENDPOINT);
  if (envUrl) {
    return envUrl;
  }
  const nodeBase = sanitizeBaseUrl(readProcessEnv("VITE_API_BASE_URL"));
  if (nodeBase) {
    return nodeBase;
  }
  const nodeUrl = sanitizeBaseUrl(readProcessEnv("VITE_API_URL") || readProcessEnv("API_ENDPOINT"));
  if (nodeUrl) {
    return nodeUrl;
  }
  const fallbackBrand = inferBrand(importMeta);
  const fallback = buildUnifiedBaseUrl(fallbackBrand, inferStage(importMeta));
  return sanitizeBaseUrl(fallback);
};
var AuthProvider = ({
  children,
  authApiBaseUrl,
  allowDevBypass = false,
  allowedDomains = [],
  allowedEmails = [],
  appId,
  appDefaultRole = "none",
  appEnforcement = "enforced",
  devAppEntitlements
}) => {
  const apiBaseUrl = resolveApiBaseUrl(authApiBaseUrl);
  const normalizedDomains = useMemo(
    () => allowedDomains.map((domain) => domain.trim().toLowerCase()).filter((domain) => domain.length > 0),
    [allowedDomains]
  );
  const normalizedEmails = useMemo(
    () => allowedEmails.map((email) => email.trim().toLowerCase()).filter((email) => email.length > 0),
    [allowedEmails]
  );
  const restrictionsEnabled = normalizedDomains.length > 0 || normalizedEmails.length > 0;
  const isEmailAllowed = (email) => {
    if (!restrictionsEnabled) {
      return true;
    }
    if (!email) return false;
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) return false;
    if (normalizedEmails.includes(normalizedEmail)) {
      return true;
    }
    return normalizedDomains.some((domain) => normalizedEmail.endsWith(`@${domain}`));
  };
  const DEV_BYPASS_STORAGE_KEY = "es-dev-bypass-user";
  const readStoredDevBypassUser = useCallback(() => {
    if (!allowDevBypass || typeof window === "undefined") {
      return null;
    }
    try {
      const raw = window.localStorage.getItem(DEV_BYPASS_STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const parsed = normalizeAuthUser(JSON.parse(raw));
      if (parsed) {
        return {
          ...parsed,
          appEntitlements: normalizeAppEntitlementManifest(devAppEntitlements) || parsed.appEntitlements
        };
      }
    } catch (error) {
      console.warn("Failed to read dev bypass user from storage", error);
    }
    return null;
  }, [allowDevBypass, devAppEntitlements]);
  const writeStoredDevBypassUser = useCallback(
    (next) => {
      if (!allowDevBypass || typeof window === "undefined") {
        return;
      }
      try {
        if (next) {
          window.localStorage.setItem(DEV_BYPASS_STORAGE_KEY, JSON.stringify(next));
        } else {
          window.localStorage.removeItem(DEV_BYPASS_STORAGE_KEY);
        }
      } catch (error) {
        console.warn("Failed to persist dev bypass user", error);
      }
    },
    [allowDevBypass]
  );
  const [user, setUser] = useState(() => readStoredDevBypassUser());
  const [isLoading, setIsLoading] = useState(() => readStoredDevBypassUser() === null);
  const [authError, setAuthError] = useState(null);
  const [authSource, setAuthSource] = useState(() => readStoredDevBypassUser() ? "dev-bypass" : "unknown");
  const hasAuthenticatedRef = useRef(readStoredDevBypassUser() !== null);
  const authRevisionRef = useRef(0);
  const cookieRequestRef = useRef(Promise.resolve());
  const fetchCookieSession = useCallback((...args) => {
    const pending = cookieRequestRef.current.then(() => fetch(...args));
    cookieRequestRef.current = pending.then(() => void 0, () => void 0);
    return pending;
  }, []);
  const clearAuthError = useCallback(() => {
    setAuthError(null);
  }, []);
  const handleAuthExpiration = useCallback(() => {
    if (!hasAuthenticatedRef.current) {
      setUser(null);
      setAuthSource("none");
      setIsLoading(false);
      return;
    }
    authRevisionRef.current++;
    hasAuthenticatedRef.current = false;
    setAuthError((prev) => prev ?? "Your session expired. Please sign in again.");
    setUser(null);
    setAuthSource("none");
    setIsLoading(false);
  }, []);
  const fetchSession = useCallback(async () => {
    const revision = authRevisionRef.current;
    const storedDevBypassUser = readStoredDevBypassUser();
    if (storedDevBypassUser) {
      setUser(storedDevBypassUser);
      setAuthSource("dev-bypass");
      hasAuthenticatedRef.current = true;
      setIsLoading(false);
      return;
    }
    if (!apiBaseUrl) {
      setAuthSource("none");
      setIsLoading(false);
      return;
    }
    try {
      const response = await fetchCookieSession(`${apiBaseUrl}/auth/me`, {
        method: "GET",
        credentials: "include",
        cache: "no-store"
      });
      if (revision !== authRevisionRef.current) return;
      if (response.status === 401) {
        handleAuthExpiration();
        return;
      }
      if (!response.ok) {
        setUser(null);
        setAuthSource("none");
        setIsLoading(false);
        return;
      }
      const payload = await response.json();
      if (revision !== authRevisionRef.current) return;
      const nextUser = normalizeAuthUser(payload.user);
      setUser(nextUser);
      if (nextUser) {
        setAuthSource("cookie");
        hasAuthenticatedRef.current = true;
        setAuthError(null);
      } else {
        setAuthSource("none");
      }
    } catch (error) {
      if (revision !== authRevisionRef.current) return;
      console.warn("Failed to fetch session", error);
      setUser(null);
      setAuthSource("none");
    } finally {
      if (revision === authRevisionRef.current) setIsLoading(false);
    }
  }, [apiBaseUrl, fetchCookieSession, handleAuthExpiration, readStoredDevBypassUser]);
  useEffect(() => {
    fetchSession();
    return () => {
      authRevisionRef.current++;
    };
  }, [fetchSession]);
  useEffect(() => {
    if (typeof window === "undefined" || !apiBaseUrl) {
      return void 0;
    }
    const nativeFetch = window.fetch;
    const monitoredFetch = async (...args) => {
      const revision = authRevisionRef.current;
      const response = await nativeFetch(...args);
      try {
        const [requestInfo] = args;
        let requestUrl = "";
        if (typeof requestInfo === "string") {
          requestUrl = requestInfo;
        } else if (typeof URL !== "undefined" && requestInfo instanceof URL) {
          requestUrl = requestInfo.href;
        } else if (typeof Request !== "undefined" && requestInfo instanceof Request) {
          requestUrl = requestInfo.url;
        }
        if (revision === authRevisionRef.current && response.status === 401 && requestUrl && requestUrl.startsWith(apiBaseUrl)) {
          if (!readStoredDevBypassUser()) {
            handleAuthExpiration();
          }
        }
      } catch (error) {
        console.warn("Auth interceptor error", error);
      }
      return response;
    };
    window.fetch = monitoredFetch;
    return () => {
      window.fetch = nativeFetch;
    };
  }, [apiBaseUrl, fetchCookieSession, handleAuthExpiration, readStoredDevBypassUser]);
  const login = useCallback(
    async ({ code, diagnosticId, devBypass, staticLogin, email, password, vendorPasswordIdToken }) => {
      const revision = ++authRevisionRef.current;
      setUser(null);
      setAuthSource("none");
      setIsLoading(false);
      hasAuthenticatedRef.current = false;
      const assertCurrent = () => {
        if (revision !== authRevisionRef.current) throw new DOMException("Sign-in changed.", "AbortError");
      };
      if (allowDevBypass && devBypass) {
        const mockUser = {
          email: "dev@easystreetbrands.com",
          name: "Development User",
          appEntitlements: normalizeAppEntitlementManifest(devAppEntitlements)
        };
        setUser(mockUser);
        setAuthSource("dev-bypass");
        writeStoredDevBypassUser(mockUser);
        hasAuthenticatedRef.current = true;
        setAuthError(null);
        return;
      }
      if (!apiBaseUrl) {
        throw new Error("Auth API base URL is not configured");
      }
      if (staticLogin && (!email || !password)) {
        throw new Error("Email and password are required");
      }
      if (vendorPasswordIdToken && (staticLogin || code)) throw new Error("Choose one sign-in method.");
      if (!staticLogin && !code && !vendorPasswordIdToken) {
        throw new Error("Missing authorization code");
      }
      const response = await fetchCookieSession(`${apiBaseUrl}${vendorPasswordIdToken ? "/auth/vendor-identity/password/session" : staticLogin ? "/auth/static-login" : "/auth/google"}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(vendorPasswordIdToken ? { idToken: vendorPasswordIdToken } : staticLogin ? { email, password } : { code, ...diagnosticId ? { diagnostic_id: diagnosticId } : {} })
      });
      assertCurrent();
      if (!response.ok) {
        const text = await response.text();
        assertCurrent();
        let message = text || "Authentication failed";
        try {
          const parsed = JSON.parse(text);
          message = parsed.message || message;
        } catch {
        }
        throw Object.assign(new Error(message), { status: response.status });
      }
      const payload = await response.json();
      assertCurrent();
      const authedUser = normalizeAuthUser(payload.user);
      const approvedVendor = authedUser?.identityType === "vendor" && authedUser.vendorAccess?.active && Date.parse(authedUser.vendorAccess.expiresAt) > Date.now() && authedUser.appEntitlements?.source === "vendor-access";
      if (vendorPasswordIdToken && (!approvedVendor || !authedUser?.externalIdentity)) throw new Error("External dashboard sign-in is not authorized.");
      if (!approvedVendor && !isEmailAllowed(authedUser?.email)) {
        setUser(null);
        throw new Error("USER_NOT_AUTHORIZED");
      }
      if (!authedUser) {
        setUser(null);
        throw new Error("Authentication response did not include a valid user");
      }
      setUser(authedUser);
      setAuthSource("cookie");
      writeStoredDevBypassUser(null);
      hasAuthenticatedRef.current = true;
      setAuthError(null);
    },
    [allowDevBypass, apiBaseUrl, devAppEntitlements, fetchCookieSession, isEmailAllowed, writeStoredDevBypassUser]
  );
  const logout = useCallback(async () => {
    authRevisionRef.current++;
    setUser(null);
    setAuthSource("none");
    writeStoredDevBypassUser(null);
    hasAuthenticatedRef.current = false;
    setAuthError(null);
    setIsLoading(false);
    try {
      googleLogout();
    } catch (error) {
      console.warn("Google logout failed", error);
    }
    if (apiBaseUrl) {
      try {
        await fetchCookieSession(`${apiBaseUrl}/auth/logout`, {
          method: "POST",
          credentials: "include"
        });
      } catch (error) {
        console.warn("Failed to call logout endpoint", error);
      }
    }
  }, [apiBaseUrl, fetchCookieSession, writeStoredDevBypassUser]);
  return /* @__PURE__ */ jsx(
    AuthContext.Provider,
    {
      value: {
        user,
        apiBaseUrl,
        login,
        logout,
        isLoading,
        authError,
        authSource,
        applicationPolicy: appId ? { appId, defaultRole: appDefaultRole, enforcement: appEnforcement } : void 0,
        clearAuthError
      },
      children
    }
  );
};
var useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// src/auth/ProtectedRoute.tsx
import { Navigate, useLocation } from "react-router-dom";
import { Fragment, jsx as jsx2, jsxs } from "react/jsx-runtime";
var DefaultLoadingFallback = ({ message }) => /* @__PURE__ */ jsxs("div", { className: "es-auth-loading", children: [
  /* @__PURE__ */ jsx2(
    "style",
    {
      dangerouslySetInnerHTML: {
        __html: `
          .es-auth-loading {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f5f5f5;
            font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            color: #111;
          }

          .es-auth-card {
            background: #fff;
            border: 1px solid #e0e0e0;
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 2rem 2.5rem;
            text-align: center;
            box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
          }

          .es-auth-spinner {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            border: 4px solid #111;
            border-bottom-color: transparent;
            animation: es-auth-spin 0.8s linear infinite;
            margin: 0 auto;
          }

          @keyframes es-auth-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          @media (prefers-reduced-motion: reduce) {
            .es-auth-spinner { animation: none; }
          }

          .es-auth-label {
            margin-top: 1rem;
            font-weight: 500;
            letter-spacing: 0.5px;
          }

          .es-auth-warning {
            display: inline-block;
            margin-top: 0.5rem;
            font-size: 0.85rem;
            color: #b45309;
          }
        `
      }
    }
  ),
  /* @__PURE__ */ jsxs("div", { className: "es-auth-card", children: [
    /* @__PURE__ */ jsx2("div", { className: "es-auth-spinner", "aria-hidden": "true" }),
    /* @__PURE__ */ jsx2("p", { className: "es-auth-label", children: "Checking your session\u2026" }),
    message && /* @__PURE__ */ jsx2("p", { className: "es-auth-warning", children: message })
  ] })
] });
var DefaultAccessDenied = ({ appId, grantedRole, requiredRole }) => /* @__PURE__ */ jsxs("main", { className: "es-access-denied", children: [
  /* @__PURE__ */ jsx2(
    "style",
    {
      dangerouslySetInnerHTML: {
        __html: `
          .es-access-denied {
            min-height: 100vh;
            display: grid;
            place-items: center;
            padding: 24px;
            background: #f6f7f8;
            color: #161616;
            font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          }
          .es-access-denied__card {
            width: min(100%, 520px);
            padding: 32px;
            border: 1px solid #d9dde2;
            border-top: 4px solid #c43f0b;
            background: #fff;
            box-shadow: 0 18px 45px rgba(18, 24, 33, 0.1);
          }
          .es-access-denied__eyebrow {
            margin: 0 0 10px;
            color: #a5340a;
            font-size: 12px;
            font-weight: 800;
            letter-spacing: 0.12em;
            text-transform: uppercase;
          }
          .es-access-denied h1 { margin: 0; font-size: clamp(28px, 5vw, 42px); }
          .es-access-denied p { margin: 14px 0 0; color: #5d6673; line-height: 1.6; }
          .es-access-denied code { color: #333; font-size: 0.9em; }
          .es-access-denied a {
            display: inline-flex;
            margin-top: 22px;
            color: #9f3209;
            font-weight: 700;
          }
          .es-access-denied a:focus-visible { outline: 3px solid rgba(196, 63, 11, 0.3); outline-offset: 3px; }
        `
      }
    }
  ),
  /* @__PURE__ */ jsxs("section", { className: "es-access-denied__card", "aria-labelledby": "es-access-denied-title", children: [
    /* @__PURE__ */ jsx2("p", { className: "es-access-denied__eyebrow", children: "Access required" }),
    /* @__PURE__ */ jsx2("h1", { id: "es-access-denied-title", children: "This app is not in your current access set." }),
    /* @__PURE__ */ jsxs("p", { children: [
      /* @__PURE__ */ jsx2("code", { children: appId }),
      " requires the ",
      /* @__PURE__ */ jsx2("strong", { children: requiredRole }),
      " role. Your current role is",
      " ",
      /* @__PURE__ */ jsx2("strong", { children: grantedRole }),
      "."
    ] }),
    /* @__PURE__ */ jsx2("p", { children: "Ask the app owner or an access administrator to add you to the appropriate Google Workspace group." }),
    /* @__PURE__ */ jsx2("a", { href: "/", children: "Return to Automations" })
  ] })
] });
var ProtectedRoute = ({
  children,
  redirectTo = "/login",
  loadingFallback,
  requiredAppId,
  requiredRole = "viewer",
  defaultRole,
  enforcement,
  accessDeniedFallback
}) => {
  const { user, isLoading, authError, applicationPolicy } = useAuth();
  const location = useLocation();
  if (isLoading) {
    if (loadingFallback) {
      return loadingFallback;
    }
    return /* @__PURE__ */ jsx2(DefaultLoadingFallback, { message: authError });
  }
  if (!user) {
    return /* @__PURE__ */ jsx2(Navigate, { to: redirectTo, replace: true, state: { from: location } });
  }
  const appId = requiredAppId || applicationPolicy?.appId;
  if (appId) {
    const decision = evaluateAppAccess(user.appEntitlements, appId, requiredRole, {
      defaultRole: defaultRole ?? applicationPolicy?.defaultRole ?? "none",
      enforcement: enforcement ?? applicationPolicy?.enforcement ?? "enforced"
    });
    if (!decision.allowed) {
      return accessDeniedFallback || /* @__PURE__ */ jsx2(
        DefaultAccessDenied,
        {
          appId,
          grantedRole: decision.grantedRole,
          requiredRole: decision.requiredRole
        }
      );
    }
  }
  return /* @__PURE__ */ jsx2(Fragment, { children });
};

// src/auth/LoginView.tsx
import React2, { useEffect as useEffect2, useMemo as useMemo2, useRef as useRef2, useState as useState2 } from "react";
import { useGoogleLogin, useGoogleOAuth } from "@react-oauth/google";

// src/auth/googleSignInScopes.ts
var googleSignInScopeOptions = (isolate = false) => ({
  scope: "openid email profile",
  include_granted_scopes: !isolate
});

// src/auth/signInDiagnostics.ts
var SIGNIN_CODES = [
  "none",
  "sdk_not_ready",
  "popup_failed_to_open",
  "popup_closed",
  "unknown",
  "access_denied",
  "invalid_request",
  "unauthorized_client",
  "unsupported_response_type",
  "invalid_scope",
  "server_error",
  "temporarily_unavailable",
  "network_error",
  "email_not_authorized",
  "http_error",
  "unexpected_error"
];
var newSignInReference = () => globalThis.crypto?.randomUUID?.() || "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
  const r = Math.floor(Math.random() * 16);
  return (c === "x" ? r : r & 3 | 8).toString(16);
});
function browserFamily(agent) {
  if (/Edg\//.test(agent)) return "edge";
  if (/Firefox\//.test(agent)) return "firefox";
  if (/Chrome\/|CriOS\//.test(agent)) return "chrome";
  return /Safari\//.test(agent) ? "safari" : "other";
}
function signInFailureCode(error, phase) {
  const value = error && typeof error === "object" ? error : {};
  const code = phase === "popup_error" ? value.type : value.error;
  if (typeof code === "string" && SIGNIN_CODES.includes(code))
    return code;
  if (error instanceof Error && ["USER_NOT_AUTHORIZED", "Email is not authorized"].includes(error.message))
    return "email_not_authorized";
  if (error instanceof TypeError && phase === "api_error")
    return "network_error";
  if (typeof value.status === "number" && value.status >= 400 && value.status <= 599)
    return "http_error";
  return "unexpected_error";
}
function reportSignInDiagnostic(apiBaseUrl, diagnostic) {
  const safe = {
    attempt_id: diagnostic.attempt_id,
    phase: diagnostic.phase,
    code: diagnostic.code,
    browser: diagnostic.browser
  };
  const status = diagnostic.http_status;
  if (typeof status === "number" && Number.isInteger(status) && status >= 400 && status <= 599)
    safe.http_status = status;
  try {
    sessionStorage.setItem(
      "signin-support",
      JSON.stringify({ ...safe, at: (/* @__PURE__ */ new Date()).toISOString() })
    );
  } catch {
  }
  try {
    void fetch(`${apiBaseUrl}/auth/google`, {
      method: "POST",
      credentials: "omit",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kind: "signin_diagnostic", ...safe })
    }).catch(() => void 0);
  } catch {
  }
}

// src/auth/LoginView.tsx
import { Fragment as Fragment2, jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var BASE_LOGIN_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;900&family=Open+Sans:wght@300;400;500;600;700&display=swap');

  .es-login-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    background: #f5f5f5;
    padding: 1rem;
  }

  .es-login-card {
    background: #fff;
    padding: 4rem 3rem;
    border-radius: 0;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
    text-align: center;
    max-width: 480px;
    width: 100%;
    border: 1px solid #e0e0e0;
    position: relative;
    overflow: hidden;
  }

  .es-login-logo {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    margin-bottom: 3rem;
  }

  .es-login-logo-image {
    display: block;
    width: min(220px, 70vw);
    height: auto;
  }

  .es-logo-es {
    font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 3rem;
    font-weight: 900;
    color: #000;
    letter-spacing: -2px;
  }

  .es-logo-text {
    font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 1.25rem;
    font-weight: 300;
    color: #000;
    letter-spacing: 2px;
    text-transform: uppercase;
  }

  .es-login-card h1 {
    font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 1.6rem;
    font-weight: 700;
    color: #000;
    margin-bottom: 1rem;
    letter-spacing: -0.5px;
  }

  .es-login-subtitle {
    font-family: 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    color: #666;
    margin-bottom: 3rem;
    font-size: 0.95rem;
    line-height: 1.5;
  }

  .es-login-features {
    display: none;
    margin-bottom: 2rem;
    gap: 1rem;
    justify-content: center;
  }

  .es-feature {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    color: #333;
  }

  .es-feature-icon {
    font-size: 1.15rem;
  }

  .es-google-login-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    width: 100%;
    padding: 16px 24px;
    background: #000;
    border: 2px solid #000;
    border-radius: 0;
    font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #fff;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-bottom: 1rem;
  }

  .es-google-login-button:hover {
    background: #fff;
    color: #000;
  }

  .es-login-divider {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: #777;
    font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 1px;
    text-transform: uppercase;
    margin: 1.5rem 0;
  }

  .es-login-divider::before,
  .es-login-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #ddd;
  }

  .es-static-login-form {
    display: grid;
    gap: 0.75rem;
    margin-bottom: 1rem;
    text-align: left;
  }

  .es-static-login-title {
    margin: 0 0 0.25rem;
    color: #333;
    font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    text-align: center;
  }

  .es-login-field-label {
    display: grid;
    gap: 0.35rem;
    color: #333;
    font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  .es-login-input {
    width: 100%;
    box-sizing: border-box;
    padding: 13px 14px;
    border: 1px solid #cfcfcf;
    border-radius: 0;
    background: #fff;
    color: #111;
    font-family: 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 0.95rem;
    line-height: 1.3;
  }

  .es-login-input:focus {
    border-color: #000;
    outline: 2px solid rgba(0, 0, 0, 0.12);
    outline-offset: 0;
  }

  .es-static-login-button {
    width: 100%;
    padding: 14px 20px;
    background: #fff;
    border: 2px solid #000;
    border-radius: 0;
    color: #000;
    font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .es-static-login-button:hover:not(:disabled) {
    background: #000;
    color: #fff;
  }

  .es-static-login-button:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }

  .es-google-icon {
    width: 20px;
    height: 20px;
  }

  .es-dev-bypass-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 14px 24px;
    background: #ffc107;
    border: 2px solid #ffc107;
    border-radius: 0;
    font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #000;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-bottom: 2rem;
  }

  .es-dev-bypass-button:hover {
    background: transparent;
    color: #ffc107;
  }

  .es-login-footer {
    font-size: 0.75rem;
    color: #666;
    margin-bottom: 1.5rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .es-login-error {
    margin: 0 0 1rem 0;
    font-size: 0.85rem;
    font-family: 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    color: #b91c1c;
  }

  .es-brand-links {
    display: flex;
    justify-content: center;
    gap: 1.5rem;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    flex-wrap: wrap;
  }

  .es-brand-links a {
    color: #000;
    text-decoration: none;
  }

  .es-login-separator {
    color: #999;
  }
`;
var defaultFeatures = [
  { icon: "\u{1F680}", label: "Streamline Operations" },
  { icon: "\u{1F4CA}", label: "Data-Driven Insights" },
  { icon: "\u26A1", label: "Scale Efficiently" }
];
var defaultLinks = [];
var LoginView = ({
  title = "Welcome to the Automation Portal",
  subtitle = "Sign in to access your approved automation workspace.",
  logoPrefix = "AT",
  logoText = "Automation",
  logoSrc,
  logoAlt = "",
  buttonLabel = "Sign in with Google",
  accessDeniedText = "Access denied. Please sign in with an approved Google Workspace account.",
  allowDevBypass = false,
  enableStaticLogin = false,
  staticLoginTitle = "Dashboard credentials",
  staticLoginButtonLabel = "Sign in",
  features = defaultFeatures,
  brandLinks = defaultLinks,
  supportText = "Authorized team members only",
  onLoginSuccess,
  isolateGoogleSignInScopes = false
}) => {
  const { login, authError, clearAuthError, apiBaseUrl } = useAuth();
  const { scriptLoadedSuccessfully } = useGoogleOAuth();
  const attempt = useRef2("");
  const [supportDetails, setSupportDetails] = useState2(null);
  const [errorMessage, setErrorMessage] = useState2(null);
  const [isGooglePending, setIsGooglePending] = useState2(false);
  const [staticEmail, setStaticEmail] = useState2("");
  const [staticPassword, setStaticPassword] = useState2("");
  const [isStaticSubmitting, setIsStaticSubmitting] = useState2(false);
  useEffect2(() => {
    if (authError) {
      setErrorMessage(authError);
    }
  }, [authError]);
  const handleAuthError = (error) => {
    if (error instanceof Error && ["USER_NOT_AUTHORIZED", "Email is not authorized"].includes(error.message)) {
      setErrorMessage(accessDeniedText);
      return;
    }
    if (error instanceof Error && error.message === "Invalid email or password") {
      setErrorMessage("Invalid email or password.");
      return;
    }
    setErrorMessage("Login failed. Please try again or contact an administrator.");
  };
  const report = (phase, code = "none", status) => {
    if (!attempt.current) attempt.current = newSignInReference();
    const diagnostic = {
      attempt_id: attempt.current,
      phase,
      code,
      browser: browserFamily(navigator.userAgent),
      ...typeof status === "number" && Number.isInteger(status) && status >= 400 && status <= 599 ? { http_status: status } : {}
    };
    setSupportDetails({ ...diagnostic, at: (/* @__PURE__ */ new Date()).toISOString() });
    reportSignInDiagnostic(apiBaseUrl, diagnostic);
  };
  const googleFailure = (error, phase) => {
    setIsGooglePending(false);
    const code = signInFailureCode(error, phase);
    const status = error && typeof error === "object" ? error.status : void 0;
    report(phase, code, status);
    const messages = {
      sdk_not_ready: "Google sign-in could not load. Check your connection or browser extensions, then reload and try again.",
      popup_failed_to_open: "Your browser blocked the Google sign-in window. Allow popups for this site and try again.",
      popup_closed: "The Google sign-in window closed before sign-in finished. Please try again.",
      access_denied: "Google sign-in was canceled or denied. Please try again with your approved account.",
      network_error: "Sign-in could not reach our server. Check your connection and try again.",
      email_not_authorized: accessDeniedText
    };
    if (messages[code]) setErrorMessage(messages[code]);
    else handleAuthError(error);
  };
  useEffect2(() => {
    return () => {
      clearAuthError();
    };
  }, [clearAuthError]);
  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    ux_mode: "popup",
    redirect_uri: "postmessage",
    ...googleSignInScopeOptions(isolateGoogleSignInScopes),
    // `select_account` lets users pick which Google account to use but
    // skips the consent screen for accounts that have already approved
    // the app. Without this override, @react-oauth/google sends
    // `prompt=consent` by default on the auth-code flow and Google
    // re-prompts for permissions on every sign-in.
    prompt: "select_account",
    onSuccess: async (response) => {
      try {
        setErrorMessage(null);
        clearAuthError();
        report("oauth_response");
        report("api_request");
        await login({ code: response.code, diagnosticId: attempt.current });
        report("succeeded");
        setIsGooglePending(false);
        onLoginSuccess?.();
      } catch (error) {
        googleFailure(error, "api_error");
      }
    },
    onError: (error) => {
      googleFailure(error, "oauth_error");
    },
    onNonOAuthError: (error) => googleFailure(error, "popup_error")
  });
  const handleGoogleLogin = () => {
    if (allowDevBypass) {
      console.log("Google login disabled because dev bypass is active.");
      return;
    }
    attempt.current = newSignInReference();
    setIsGooglePending(true);
    setErrorMessage(null);
    clearAuthError();
    report("started");
    if (!scriptLoadedSuccessfully) {
      googleFailure({ error: "sdk_not_ready" }, "sdk_error");
      return;
    }
    try {
      googleLogin();
    } catch (error) {
      googleFailure(error, "popup_error");
    }
  };
  const handleDevBypass = async () => {
    try {
      setErrorMessage(null);
      setSupportDetails(null);
      clearAuthError();
      await login({ devBypass: true });
      onLoginSuccess?.();
    } catch (error) {
      handleAuthError(error);
    }
  };
  const handleStaticLogin = async (event) => {
    event.preventDefault();
    try {
      setIsStaticSubmitting(true);
      setErrorMessage(null);
      setSupportDetails(null);
      clearAuthError();
      await login({
        staticLogin: true,
        email: staticEmail.trim(),
        password: staticPassword
      });
      onLoginSuccess?.();
    } catch (error) {
      handleAuthError(error);
    } finally {
      setIsStaticSubmitting(false);
    }
  };
  const showFeatures = useMemo2(() => features && features.length > 0, [features]);
  const showBrandLinks = useMemo2(() => brandLinks && brandLinks.length > 0, [brandLinks]);
  return /* @__PURE__ */ jsxs2("div", { className: "es-login-container", children: [
    /* @__PURE__ */ jsx3("style", { dangerouslySetInnerHTML: { __html: BASE_LOGIN_STYLES } }),
    /* @__PURE__ */ jsxs2("div", { className: "es-login-card", children: [
      /* @__PURE__ */ jsx3("div", { className: "es-login-logo", children: logoSrc ? /* @__PURE__ */ jsx3("img", { className: "es-login-logo-image", src: logoSrc, alt: logoAlt }) : /* @__PURE__ */ jsxs2(Fragment2, { children: [
        /* @__PURE__ */ jsx3("span", { className: "es-logo-es", children: logoPrefix }),
        /* @__PURE__ */ jsx3("span", { className: "es-logo-text", children: logoText })
      ] }) }),
      /* @__PURE__ */ jsx3("h1", { children: title }),
      subtitle && /* @__PURE__ */ jsx3("p", { className: "es-login-subtitle", children: subtitle }),
      showFeatures && /* @__PURE__ */ jsx3("div", { className: "es-login-features", children: features.map((feature) => /* @__PURE__ */ jsxs2("div", { className: "es-feature", children: [
        /* @__PURE__ */ jsx3("span", { className: "es-feature-icon", "aria-hidden": "true", children: feature.icon }),
        /* @__PURE__ */ jsx3("span", { children: feature.label })
      ] }, feature.label)) }),
      /* @__PURE__ */ jsxs2("button", { type: "button", onClick: handleGoogleLogin, className: "es-google-login-button", disabled: isGooglePending, children: [
        /* @__PURE__ */ jsxs2("svg", { className: "es-google-icon", viewBox: "0 0 24 24", "aria-hidden": "true", children: [
          /* @__PURE__ */ jsx3(
            "path",
            {
              fill: "#4285F4",
              d: "M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            }
          ),
          /* @__PURE__ */ jsx3(
            "path",
            {
              fill: "#34A853",
              d: "M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            }
          ),
          /* @__PURE__ */ jsx3(
            "path",
            {
              fill: "#FBBC05",
              d: "M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            }
          ),
          /* @__PURE__ */ jsx3(
            "path",
            {
              fill: "#EA4335",
              d: "M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            }
          )
        ] }),
        buttonLabel
      ] }),
      enableStaticLogin && /* @__PURE__ */ jsxs2(Fragment2, { children: [
        /* @__PURE__ */ jsx3("div", { className: "es-login-divider", children: "or" }),
        /* @__PURE__ */ jsxs2("form", { className: "es-static-login-form", onSubmit: handleStaticLogin, children: [
          staticLoginTitle && /* @__PURE__ */ jsx3("p", { className: "es-static-login-title", children: staticLoginTitle }),
          /* @__PURE__ */ jsxs2("label", { className: "es-login-field-label", children: [
            "Email",
            /* @__PURE__ */ jsx3(
              "input",
              {
                className: "es-login-input",
                type: "email",
                value: staticEmail,
                autoComplete: "username",
                required: true,
                onChange: (event) => setStaticEmail(event.target.value)
              }
            )
          ] }),
          /* @__PURE__ */ jsxs2("label", { className: "es-login-field-label", children: [
            "Password",
            /* @__PURE__ */ jsx3(
              "input",
              {
                className: "es-login-input",
                type: "password",
                value: staticPassword,
                autoComplete: "current-password",
                required: true,
                onChange: (event) => setStaticPassword(event.target.value)
              }
            )
          ] }),
          /* @__PURE__ */ jsx3("button", { type: "submit", className: "es-static-login-button", disabled: isStaticSubmitting, children: isStaticSubmitting ? "Signing in..." : staticLoginButtonLabel })
        ] })
      ] }),
      errorMessage && /* @__PURE__ */ jsx3("p", { className: "es-login-error", children: errorMessage }),
      errorMessage && supportDetails && /* @__PURE__ */ jsxs2("details", { className: "es-login-support", style: { textAlign: "left", fontSize: "12px", overflowWrap: "anywhere" }, children: [
        /* @__PURE__ */ jsxs2("summary", { children: [
          "Sign-in support reference: ",
          supportDetails.attempt_id
        ] }),
        /* @__PURE__ */ jsx3("p", { children: "Share these details with your administrator." }),
        /* @__PURE__ */ jsx3("pre", { style: { whiteSpace: "pre-wrap" }, children: JSON.stringify(supportDetails, null, 2) })
      ] }),
      allowDevBypass && /* @__PURE__ */ jsx3("button", { type: "button", onClick: handleDevBypass, className: "es-dev-bypass-button", children: "Development Login (Bypass)" }),
      supportText && /* @__PURE__ */ jsx3("p", { className: "es-login-footer", children: supportText }),
      showBrandLinks && /* @__PURE__ */ jsx3("div", { className: "es-brand-links", children: brandLinks.map((link, index) => /* @__PURE__ */ jsxs2(React2.Fragment, { children: [
        /* @__PURE__ */ jsx3("a", { href: link.href, target: "_blank", rel: "noopener noreferrer", children: link.label }),
        index < brandLinks.length - 1 && /* @__PURE__ */ jsx3("span", { className: "es-login-separator", children: "\u2022" })
      ] }, link.href)) })
    ] })
  ] });
};

// src/utils/api.ts
var ApiClient = class {
  constructor(baseUrl) {
    this.baseUrl = baseUrl;
    this.headers = {
      "Content-Type": "application/json"
    };
  }
  setAuthToken(token) {
    this.headers["Authorization"] = `Bearer ${token}`;
  }
  clearAuthToken() {
    delete this.headers["Authorization"];
  }
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      ...options,
      headers: {
        ...this.headers,
        ...options.headers || {}
      }
    };
    const response = await fetch(url, config);
    if (!response.ok) {
      throw new Error(`API Error: ${response.statusText}`);
    }
    return response.json();
  }
  get(endpoint) {
    return this.request(endpoint, { method: "GET" });
  }
  post(endpoint, body) {
    return this.request(endpoint, {
      method: "POST",
      body: JSON.stringify(body)
    });
  }
  put(endpoint, body) {
    return this.request(endpoint, {
      method: "PUT",
      body: JSON.stringify(body)
    });
  }
  delete(endpoint) {
    return this.request(endpoint, { method: "DELETE" });
  }
};

// src/utils/auth.ts
var AUTH_TOKEN_KEY = "auth_token";
var USER_KEY = "user";
var authUtils = {
  getToken() {
    return localStorage.getItem(AUTH_TOKEN_KEY) || localStorage.getItem("token");
  },
  setToken(token) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    localStorage.setItem("token", token);
  },
  removeToken() {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem("token");
  },
  getUser() {
    const userStr = localStorage.getItem(USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  },
  setUser(user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },
  removeUser() {
    localStorage.removeItem(USER_KEY);
  },
  isAuthenticated() {
    return !!this.getToken();
  },
  logout() {
    this.removeToken();
    this.removeUser();
    window.location.href = "/login";
  }
};

// src/components/LoadingSpinner.tsx
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var LoadingSpinner = ({
  size = "medium",
  message
}) => {
  const sizeClasses = {
    small: "w-8 h-8",
    medium: "w-12 h-12",
    large: "w-16 h-16"
  };
  return /* @__PURE__ */ jsxs3("div", { className: "loading-spinner-container", children: [
    /* @__PURE__ */ jsx4("div", { className: `loading-spinner ${sizeClasses[size]}` }),
    message && /* @__PURE__ */ jsx4("p", { className: "loading-message", children: message })
  ] });
};

// src/components/ErrorBoundary.tsx
import { Component } from "react";
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var ErrorBoundary = class extends Component {
  constructor() {
    super(...arguments);
    this.state = {
      hasError: false,
      error: null
    };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return /* @__PURE__ */ jsxs4("div", { className: "error-boundary", children: [
        /* @__PURE__ */ jsx5("h2", { children: "Oops, something went wrong" }),
        /* @__PURE__ */ jsx5("details", { style: { whiteSpace: "pre-wrap" }, children: this.state.error && this.state.error.toString() })
      ] });
    }
    return this.props.children;
  }
};

// src/influencer/client.ts
var buildQueryString = (params = {}) => {
  const query = Object.entries(params).filter(([, value]) => value !== void 0 && value !== null && value !== "").map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`).join("&");
  return query ? `?${query}` : "";
};
var createInfluencerClient = (config) => {
  const fetchImpl = config.fetchFn ?? fetch;
  const authorizedFetch = async (path, init = {}) => {
    const headers = {
      "Content-Type": "application/json",
      ...init.headers
    };
    if (config.apiKey) {
      headers["x-api-key"] = config.apiKey;
    }
    if (config.brandId) {
      headers["x-brand-id"] = config.brandId;
    }
    if (config.environment) {
      headers["x-environment"] = config.environment;
    }
    if (config.getAccessToken) {
      const token = await config.getAccessToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }
    const response = await fetchImpl(`${config.baseUrl}${path}`, {
      ...init,
      headers
    });
    if (!response.ok) {
      const body = await response.text();
      throw new Error(`Influencer API request failed (${response.status}): ${body}`);
    }
    if (response.status === 204) {
      return void 0;
    }
    return response.json();
  };
  return {
    creators: {
      list: (filters) => authorizedFetch(`/influencers/creators${buildQueryString(filters ?? {})}`),
      get: (id) => authorizedFetch(`/influencers/creators/${id}`),
      create: (payload) => authorizedFetch(`/influencers/creators`, {
        method: "POST",
        body: JSON.stringify(payload)
      }),
      update: (id, payload) => authorizedFetch(`/influencers/creators/${id}`, {
        method: "PUT",
        body: JSON.stringify(payload)
      })
    },
    campaigns: {
      list: (filters) => authorizedFetch(`/influencers/campaigns${buildQueryString(filters ?? {})}`),
      get: (id) => authorizedFetch(`/influencers/campaigns/${id}`),
      create: (payload) => authorizedFetch(`/influencers/campaigns`, {
        method: "POST",
        body: JSON.stringify(payload)
      }),
      update: (id, payload) => authorizedFetch(`/influencers/campaigns/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload)
      })
    },
    briefs: {
      list: (filters) => authorizedFetch(`/influencers/briefs${buildQueryString(filters ?? {})}`),
      get: (id) => authorizedFetch(`/influencers/briefs/${id}`),
      create: (payload) => authorizedFetch(`/influencers/briefs`, {
        method: "POST",
        body: JSON.stringify(payload)
      }),
      dispatch: (payload) => authorizedFetch(`/influencers/briefs/${payload.briefId}/dispatch`, {
        method: "POST",
        body: JSON.stringify(payload)
      })
    },
    metrics: {
      listByCreator: (creatorId, params) => authorizedFetch(
        `/influencers/metrics${buildQueryString({ creatorId, ...params })}`
      )
    }
  };
};

// src/reporting/ColumnSizing.tsx
import {
  useEffect as useEffect3,
  useRef as useRef3,
  useState as useState3
} from "react";
import { jsx as jsx6 } from "react/jsx-runtime";
function useReportingColumnSizing(saved, onSave, defaultWidths = {}, options = {
  bounds: () => ({ min: 64, max: 900 }),
  defaultWidth: () => 132
}) {
  const columnWidthBounds = options.bounds, defaultWidth = options.defaultWidth;
  const clamp = (id, width2) => {
    const { min, max } = columnWidthBounds(id);
    return Math.round(Math.max(min, Math.min(max, width2)));
  };
  const tableRef = useRef3(null);
  const [live, setLive] = useState3(null);
  const drag = useRef3(null);
  const widths = live || saved;
  const resizing = live !== null;
  const fixed = Object.keys(widths).length > 0;
  const width = (id) => id === "compare" ? 34 : widths[id] ?? defaultWidths[id] ?? defaultWidth(id);
  const snapshot = () => {
    const next = { ...saved };
    tableRef.current?.querySelectorAll(
      "thead tr:first-child th[data-column-id], thead tr:first-child th[data-resize-column-id]"
    ).forEach((cell) => {
      const id = cell.dataset.resizeColumnId || cell.dataset.columnId;
      if (id !== "compare")
        next[id] = clamp(id, cell.getBoundingClientRect().width);
    });
    return next;
  };
  const finish = (commit) => {
    const current = drag.current;
    drag.current = null;
    if (commit && current) onSave(current.widths, current.id);
    setLive(null);
  };
  useEffect3(() => {
    if (!resizing) return;
    const { cursor, userSelect } = document.body.style;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.cursor = cursor;
      document.body.style.userSelect = userSelect;
    };
  }, [resizing]);
  return {
    tableRef,
    bounds: options.bounds,
    fixed,
    resizing,
    width,
    tableStyle: (ids) => fixed ? {
      tableLayout: "fixed",
      width: ids.reduce((sum, id) => sum + width(id), 0)
    } : void 0,
    colStyle: (id) => fixed ? { width: width(id) } : void 0,
    start: (id, event) => {
      if (event.button !== 0 || drag.current) return;
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.focus();
      const next = snapshot();
      drag.current = {
        id,
        pointerId: event.pointerId,
        x: event.clientX,
        start: next[id] ?? defaultWidth(id),
        widths: next
      };
      event.currentTarget.setPointerCapture(event.pointerId);
      setLive(next);
    },
    move: (event) => {
      const current = drag.current;
      if (!current || current.pointerId !== event.pointerId) return;
      current.widths = {
        ...current.widths,
        [current.id]: clamp(
          current.id,
          current.start + event.clientX - current.x
        )
      };
      setLive(current.widths);
    },
    end: (event, commit) => {
      if (drag.current?.pointerId !== event.pointerId) return;
      finish(commit);
      if (event.currentTarget.hasPointerCapture(event.pointerId))
        event.currentTarget.releasePointerCapture(event.pointerId);
    },
    key: (id, event) => {
      if (event.key === "Escape" && drag.current) {
        const pointerId = drag.current.pointerId;
        event.preventDefault();
        event.stopPropagation();
        finish(false);
        if (event.currentTarget.hasPointerCapture(pointerId))
          event.currentTarget.releasePointerCapture(pointerId);
        return;
      }
      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key))
        return;
      event.preventDefault();
      event.stopPropagation();
      const next = snapshot(), { min, max } = columnWidthBounds(id);
      const delta = (event.shiftKey ? 40 : 10) * (event.key === "ArrowLeft" ? -1 : 1);
      next[id] = event.key === "Home" ? min : event.key === "End" ? max : clamp(id, (next[id] ?? defaultWidth(id)) + delta);
      onSave(next, id);
    }
  };
}
function ReportingColumnResizeHandle({
  id,
  label,
  sizing,
  className = "reporting-column-resizer"
}) {
  const ref = useRef3(null);
  const [measured, setMeasured] = useState3();
  useEffect3(() => {
    const cell = ref.current?.closest("th");
    if (!cell || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(
      () => setMeasured(Math.round(cell.getBoundingClientRect().width))
    );
    observer.observe(cell);
    return () => observer.disconnect();
  }, []);
  const { min, max } = sizing.bounds(id);
  return /* @__PURE__ */ jsx6(
    "span",
    {
      ref,
      className,
      role: "separator",
      tabIndex: 0,
      "aria-label": `Resize ${label} column`,
      "aria-orientation": "vertical",
      "aria-valuemin": min,
      "aria-valuemax": max,
      "aria-valuenow": Math.round(
        Math.max(min, Math.min(max, measured ?? sizing.width(id)))
      ),
      title: "Drag to resize. Arrow keys adjust width; Shift adjusts faster. Escape cancels a drag.",
      onPointerDown: (e) => sizing.start(id, e),
      onPointerMove: sizing.move,
      onPointerUp: (e) => sizing.end(e, true),
      onPointerCancel: (e) => sizing.end(e, false),
      onLostPointerCapture: (e) => sizing.end(e, false),
      onKeyDown: (e) => sizing.key(id, e),
      onClick: (e) => e.stopPropagation()
    }
  );
}
export {
  APP_ROLES,
  AUTH_TOKEN_KEY,
  ApiClient,
  AuthProvider,
  ErrorBoundary,
  LoadingSpinner,
  LoginView,
  ProtectedRoute,
  ReportingColumnResizeHandle,
  USER_KEY,
  authUtils,
  compareAppRoles,
  createInfluencerClient,
  evaluateAppAccess,
  normalizeAppEntitlementManifest,
  normalizeAuthUser,
  resolveApiBaseUrl,
  useAuth,
  useReportingColumnSizing
};
